import FieldWithDateValueController from '../fieldWithDateValue.controller';

class DateTimeFieldController extends FieldWithDateValueController {
  constructor($scope, $q, $translate, $log, $timeout, DateTimeService, LogService) {
    "ngInject";
    super($scope, $q, $translate, $log, $timeout, DateTimeService, LogService);
    this._datePickerOpened = false;
    this._datePickerOptions = {};

    // See: https://github.com/Gillardo/bootstrap-ui-datetime-picker
    this._defaultButtonBar = {
      show: true,
      now: {
        show: true,
        text: 'Now',
        cls: 'btn-sm btn-default'
      },
      today: {
        show: true,
        text: 'Today',
        cls: 'btn-sm btn-default'
      },
      clear: {
        show: true,
        text: 'Clear',
        cls: 'btn-sm btn-default'
      },
      date: {
        show: true,
        text: 'Date',
        cls: 'btn-sm btn-default'
      },
      time: {
        show: true,
        text: 'Time',
        cls: 'btn-sm btn-default'
      },
      close: {
        show: true,
        text: 'Close',
        cls: 'btn-sm btn-default'
      }
    };
    this._buttonBar = null;
  }

  $onInit() {
    super.$onInit();

    this.dateValue = this.value;

    this.$scope.$on('LanguageServiceChangedLanguage', (language) => {
      console.debug(`DateTimeFieldController got language changed notification; new language:`, language);
      this._updateButtonBar();
    });

    this._updateButtonBar();
  }

  _updateButtonBar() {
    console.debug(`_updateButtonBar()`);
    const buttonNameToTranslationKeyMap = {
      now: 'NOW',
      today: 'TODAY',
      clear: 'CLEAR',
      date: 'DATE',
      time: 'TIME',
      close: 'CLOSE',
    };
    const buttonNames = Object.keys(buttonNameToTranslationKeyMap);
    const translationKeys = Object.values(buttonNameToTranslationKeyMap);
    const translatePromises = translationKeys.map(k => this.$translate(k));

    return this.$q.all(translatePromises)
      .then(translatedValues => {
        console.debug(`_updateButtonBar got translated values:`, translatedValues);
        const newButtonBar = buttonNames.reduce((buttonBar, buttonName, buttonIdx) => {
          buttonBar[buttonName].text = translatedValues[buttonIdx]
          return buttonBar;
        }, {...this._defaultButtonBar});
        console.debug(`_updateButtonBar setting new button bar:`, newButtonBar);
        this._buttonBar = newButtonBar;
      });
  }

  get buttonBar() {
    return this._defaultButtonBar;
  }

  get datePickerOptions() {
    return this._datePickerOptions;
  }

  get datePickerOpened() {
    return this._datePickerOpened;
  }

  set datePickerOpened(opened) {
    this._datePickerOpened = opened;
  }

  get dateValue() {
    return this.value;
  }

  set dateValue(dateValue) {
    // TODO: Determine whether this should be parsed in server, local, or event timezone
    // TODO: Also update simple dateField

    this.value = this.DateTimeService.parseDate(dateValue);
    this._dateValueShiftedForEventTimezone = null;
    console.debug(`DateTimeFieldController set dateValue to '${this.value}' from input '${dateValue}'`, this.value, dateValue);
  }

  get dateValueShiftedForEventTimezone() {
    const dateValue = this.dateValue;
    if (!this._dateValueShiftedForEventTimezone) {
      let shiftedDate;
      if (this.eventTimezoneName) {
        shiftedDate = this.DateTimeService.shiftDateObjectFromLocal(dateValue, this.eventTimezoneName);
      } else {
        shiftedDate = dateValue;
      }
      this._dateValueShiftedForEventTimezone = shiftedDate;
    }
    return this._dateValueShiftedForEventTimezone;
  }

  set dateValueShiftedForEventTimezone(dateValue) {
    let unshiftedDate;
    if (this.eventTimezoneName) {
      unshiftedDate = this.DateTimeService.unshiftDateObjectFromLocal(dateValue, this.eventTimezoneName);
    } else {
      unshiftedDate = dateValue;
    }
    this.dateValue = unshiftedDate;
    this._dateValueShiftedForEventTimezone = dateValue;
  }

  /**
   * Return the date value, shifted so that it matches the event timezone.
   *
   * Example:
   *
   *   Event timezone is 'America/Chicago'
   *   Local timezone is 'America/Los_Angeles'
   *
   *   Datetime sent from the API is '2017-03-01 12:00:00' which we assume is relative to the event's timezone
   *
   *   DateTimeService.parseDateWithTimezone() gives us a Date object that represents '2017-03-01 12:00:00 CST -0600'
   *   However, Javascript Date objects are just seconds since epoch (Jan 1 1970 00:00:00 UTC), and do not have a
   *   specific timezone. They are generally displayed either in the local browser timezone or in UTC.
   *
   *   When printed, the datetime we've parsed with the event timezone would likely be displayed either as UTC or local
   *   time:
   *     '2017-03-01 18:00:00Z' (UTC) or
   *     '2017-03-01 10:00:00 PST -0800' (PST)
   *
   *   The bootstrap-ui and bootstrap-ui-datetime-picker libraries generally display the date/time in local time, and
   *   do not display the timezone. So, if we pass our parsed datetime directly into the library, it will display:
   *     '2017-03-01 10:00:00'
   *
   *   However, we would prefer it to display the time in the event timezone, which was 12:00.
   *
   *   So we need to "fake" this by adjusting for the time zone offset between our local timezone and the event
   *   timezone.
   *
   *   shiftDateFromLocalTimezoneToAppearAsTimezone(date, 'America/Chicago')
   *

   *     (note that Date objects are just seconds since epoch, so do not actually have any specific timezone.
   *
   *

   *   Datetime is parsed in the event timezone, so the JS Date object is '2017-03-01 12:00:00 CST -0600'
   *     (which is identical to '2017-03-01 10:00:00 PST -0800')
   *
   *   We want to shift the date so that it appears to display the correct date/time relative to the event, even though
   *   we're in a different timezone. This way, bootstrap-ui-datetime-picker does not need to do any timezone conversion
   *   itself aside from displaying in the local timezone (which is its default behavior).
   *
   *
   *
   */
  get dateValueForPicker() {
    const actualDate = this.dateValue;
  }

  get datetimePickerButtonBar() {
  }

  openDatePicker() {
    this.datePickerOpened = true;
  }
}

export default DateTimeFieldController;
